/**
 * ChatMessageRow Component
 *
 * Individual message bubble with proper metadata display.
 * - Self messages: Right-aligned bubble, timestamp above (right-aligned meta
 *   row, #2918 — the below-bubble line belongs to the reaction chip row alone)
 * - Other messages: Left-aligned, avatar left of bubble, name + timestamp above bubble
 *
 * Architecture: Follows original ChatMessage.tsx layout, updated per user feedback
 * Part of Issue #608: Chat UX improvements
 * Issue #920: Added action sheet for delete/report
 * Issue #1221: Replaced long-press with 3-dot menu button (less accidental triggers)
 */

import { useState } from 'react';
import { MoreVertical, ImageOff, Megaphone } from 'lucide-react';
import { type Message } from './ChatMessage';
import { isOptimisticId } from '../lib/optimisticId';
import Avatar from './Avatar';
import { Skeleton } from './ui/Skeleton';
import { ChatDateTime } from './ui/DateTimeDisplay';
import MessageActionSheet from './MessageActionSheet';
import MessageReactions from './MessageReactions';

/**
 * Chat image with skeleton loading + error fallback (#1511)
 * Shows shimmer skeleton while loading, error icon on failure.
 */
function ChatImage({ src, alt, single }: { src: string; alt: string; single: boolean }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-oro-surface-alt ${single ? 'w-full py-12' : 'h-32'}`}>
        <ImageOff className="w-8 h-8 text-text-muted opacity-40" />
      </div>
    );
  }

  return (
    <div className="relative">
      {!loaded && (
        <Skeleton
          variant="image"
          className={single ? 'w-full aspect-video' : 'w-full h-32'}
        />
      )}
      <img
        src={src}
        alt={alt}
        className={`${single ? 'max-w-full h-auto' : 'w-full h-32 object-cover'} ${!loaded ? 'absolute inset-0 opacity-0' : ''}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        loading="lazy"
      />
    </div>
  );
}

interface ChatMessageRowProps {
  message: Message;
  isOwn: boolean;
  clubId?: string; // #920: For message actions
  roomId?: string; // #1222: For query invalidation after delete
  // #989: room write access — gates whether reaction toggles are allowed
  // (server requires requireRoomAccess('roomId','write')). Defaults to FALSE
  // (fail closed, George r1) — the parent (ChatArea/ClubChatPage) passes the
  // room's write capability explicitly; a forgotten prop must not open chrome.
  canReact?: boolean;
  // #2708: leader/owner pin capability hint for the active room (can_pin).
  // Gates the Pin/Unpin row in the action sheet. Defaults to false.
  canPin?: boolean;
  // #2708: true when THIS message is the room's current pin (drives Pin↔Unpin).
  isPinnedMessage?: boolean;
}

// Detect emoji-only messages for larger display (#988)
// Matches Unicode emoji + optional whitespace.
// ZWJ (\u200d) and VS-16 (\ufe0f) are alternation members, not class members,
// to satisfy `no-misleading-character-class` while still matching ZWJ-joined
// compound emoji (e.g. \ud83d\udc68\u200d\ud83d\udc69\u200d\ud83d\udc67).
const isEmojiOnlyMessage = (text: string): boolean => {
  if (!text) return false;
  const emojiRegex = /^(?:[\p{Emoji}\p{Emoji_Modifier}\p{Emoji_Component}\s]|\u200d|\ufe0f)+$/u;
  return emojiRegex.test(text.trim()) && text.trim().length <= 12; // Max ~3-4 emoji
};

export function ChatMessageRow({
  message,
  isOwn,
  clubId,
  roomId,
  canReact = false,
  canPin = false,
  isPinnedMessage = false,
}: ChatMessageRowProps) {
  const isOther = !isOwn;
  const [showActionSheet, setShowActionSheet] = useState(false);

  // #989: reactions may be toggled only against a server-persisted message in a
  // room the viewer can write to (a temp/optimistic message isn't a resource
  // yet — the same guard the 3-dot actions use).
  const canReactHere =
    canReact && !!roomId && !isOptimisticId(message.id);

  // Check if message is emoji-only for larger display
  const isEmojiOnly = isEmojiOnlyMessage(message.content || '');

  // #1445: announcement messages get a distinct treatment — an accent badge
  // above the bubble plus an accent border on the bubble itself. #2784 dropped
  // the bubble glow (glow → soft): badge + border + pinned banner already carry
  // the emphasis, and the glow read as a fourth simultaneous signal. #2917 (A1,
  // owner-ratified): border + badge OUTLINE step down to `accent/40` while the
  // badge TEXT + Megaphone stay full gold — the whole gold system steps down
  // together, and the announcement still visibly outranks the #2916 whisper
  // own-chip (accent/40 stroke, no gold fill, gold-light count only). A2
  // (badge-only gold) is the fallback if this washes out on dim screens during
  // device QA.
  const isAnnouncement = !!message.isAnnouncement;

  // #1221: Show menu only when clubId and roomId are available (actions require them).
  // #2234: never on an optimistic temp-id message — it isn't a server resource
  // yet, so Delete would 404 and the rollback resurrects the bubble.
  const canShowActions =
    !!clubId && !!roomId && !!message.senderId && !isOptimisticId(message.id);
  
  return (
    <>
      <div className={`group flex gap-2 mb-2 px-3 ${isOther ? 'justify-start' : 'justify-end'}`}>
        {/* Avatar for other users only */}
        {isOther && (
          <div className="shrink-0 mt-1">
            <Avatar
              size="sm"
              name={message.sender || 'User'}
              profile_image_url={message.avatarUrl}
            />
          </div>
        )}

        {/* 3-dot menu button for own messages (left of bubble) - #1221, #1282: visible on mobile */}
        {canShowActions && isOwn && (
          <button
            onClick={() => setShowActionSheet(true)}
            className="self-center p-2 opacity-60 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 text-text-muted hover:text-text-secondary transition-opacity"
            aria-label="Message options"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        )}

        {/* Message container */}
        <div
          className={`flex flex-col ${isOther ? 'items-start' : 'items-end'} max-w-prose`}
        >
          {/* #1445: announcement badge above the bubble */}
          {isAnnouncement && (
            <div
              data-testid="announcement-badge"
              className="mb-1 inline-flex items-center gap-1 rounded-full bg-oro-surface-alt border border-accent/40 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-accent"
            >
              <Megaphone className="w-3 h-3" />
              <span>Announcement</span>
            </div>
          )}

          {/* #2918: own-message meta row — time only, right-aligned, ABOVE the
              bubble (the same meta-row-above pattern other messages already
              use). The line below any bubble is reserved for exactly one
              thing: the reaction chip row (#2916's ghost chip included). */}
          {!isOther && (
            <div
              data-testid="own-message-meta"
              className="flex w-full items-center justify-end px-1 mb-1"
            >
              <span className="text-xs text-text-muted">
                {message.timestampSeconds ? (
                  <ChatDateTime timestamp={message.timestampSeconds} className="text-xs" />
                ) : (
                  message.timestamp
                )}
              </span>
            </div>
          )}

          {/* Sender name and timestamp for other users */}
          {isOther && message.sender && (
            <div className="flex items-center justify-between gap-2 px-1 mb-1 w-full">
              <span className="text-sm text-text-secondary font-medium">
                {message.sender}
              </span>
              <span className="text-xs text-text-muted shrink-0">
                {message.timestampSeconds ? (
                  <ChatDateTime
                    timestamp={message.timestampSeconds}
                    className="text-xs"
                  />
                ) : (
                  message.timestamp
                )}
              </span>
            </div>
          )}

          {/* Image message(s) — supports single imageUrl or multiple imageUrls (#609) */}
          {(message.imageUrls && message.imageUrls.length > 0 || message.imageUrl) && (
            <div
              className={`overflow-hidden mb-1 border border-oro-border rounded-2xl ${
                message.imageUrls && message.imageUrls.length > 1 ? 'grid grid-cols-2 gap-1' : ''
              }`}
              style={{
                borderBottomLeftRadius: isOther ? '0' : '1rem',
                borderBottomRightRadius: isOther ? '1rem' : '0',
              }}
            >
              {(message.imageUrls || (message.imageUrl ? [message.imageUrl] : [])).map((url, idx) => (
                <ChatImage key={idx} src={url} alt={`Shared image ${idx + 1}`} single={!message.imageUrls || message.imageUrls.length === 1} />
              ))}
            </div>
          )}

          {/* Text bubble - emoji-only messages get larger display (#988) */}
          {message.content && (
            <div
              className={`px-4 py-3 text-text-primary rounded-2xl ${
                isOther ? 'bg-oro-chat-bubble-other-bg' : 'bg-oro-chat-bubble-self-bg'
              } ${isAnnouncement ? 'border border-accent/40 shadow-soft' : 'shadow-soft'}`}
              style={{
                borderBottomLeftRadius: isOther ? '0' : '1rem',
                borderBottomRightRadius: isOther ? '1rem' : '0',
              }}
            >
              <p className={`whitespace-pre-wrap wrap-break-word ${isEmojiOnly ? 'text-3xl leading-tight' : ''}`}>
                {message.content}
              </p>
            </div>
          )}

          {/* #989: reaction chips + quick-picker under the bubble. Renders
              nothing when there are no reactions and the viewer can't react. */}
          {roomId && (
            <MessageReactions
              roomId={roomId}
              messageId={message.id}
              reactions={message.reactions ?? []}
              canReact={canReactHere}
              alignEnd={!isOther}
            />
          )}
        </div>

        {/* 3-dot menu button for other users' messages (right of bubble) - #1221, #1282: visible on mobile */}
        {canShowActions && isOther && (
          <button
            onClick={() => setShowActionSheet(true)}
            className="self-center p-2 opacity-60 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 text-text-muted hover:text-text-secondary transition-opacity"
            aria-label="Message options"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Action Sheet for delete/report (#920, #1222).
          #2708 (George r2): `canPin` is passed raw (not `&& !isOptimisticId`)
          because this whole sheet is gated on `canShowActions`, which ALREADY
          excludes optimistic temp-id messages (see :119, "#2234: never on an
          optimistic temp-id message"). Both open triggers (:136 own, :239 other)
          are gated on it too, so the Pin row is unreachable for a temp id — a
          `!isOptimisticId` mirror here would be belt-and-suspenders. `canReact`
          carries the gate only because `canReactHere` also feeds the chip row
          (MessageReactions), which renders OUTSIDE `canShowActions`. */}
      {canShowActions && (
        <MessageActionSheet
          isOpen={showActionSheet}
          onClose={() => setShowActionSheet(false)}
          messageId={message.id}
          clubId={clubId!}
          roomId={roomId!}
          senderId={message.senderId!}
          senderName={message.sender}
          isOwnMessage={isOwn}
          messagePreview={message.content}
          reactions={message.reactions ?? []}
          canReact={canReactHere}
          canPin={canPin}
          isPinnedMessage={isPinnedMessage}
        />
      )}
    </>
  );
}
